/**
 * Detect which LHS content is already on the editor canvas (data-content-id)
 * for "Not used in editor" filtering and per-row visibility.
 */

/**
 * Safe `[attr="value"]` fragment for GrapesJS `find` / querySelector when `value` may
 * contain spaces, parentheses, or other characters that break unquoted `[attr=value]`.
 */
export function cssQuotedAttr(attrName, value) {
  if (value == null || value === "") {
    return `[${attrName}=""]`;
  }
  const escaped = String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `[${attrName}="${escaped}"]`;
}

export function getCanvasContentIdsSet(editor) {
  const frame = editor?.Canvas?.getFrameEl?.();
  const doc = frame?.contentDocument;
  const set = new Set();
  if (doc) {
    doc.querySelectorAll("[data-content-id]").forEach((el) => {
      const v = el.getAttribute("data-content-id");
      if (v) set.add(v);
    });
  }
  try {
    const w = editor?.getWrapper?.();
    if (w?.find) {
      const comps = w.find("[data-content-id]");
      const each = (c) => {
        const v = c.getAttributes?.()?.["data-content-id"];
        if (v) set.add(v);
      };
      if (comps?.forEach) {
        comps.forEach(each);
      } else if (comps?.models?.forEach) {
        comps.models.forEach(each);
      }
    }
  } catch (_) {
    /* ignore */
  }
  return set;
}

/** Normalize text for same-module duplicate detection (LHS vs canvas). */
export function normalizeModuleContentKey(text) {
  if (text == null || text === "") return "";
  return String(text).replace(/\s+/g, " ").trim();
}

function getComparableTextFromDataContentEl(el) {
  if (!el || typeof el.cloneNode !== "function") return "";
  const clone = el.cloneNode(true);
  clone.querySelectorAll?.("sup")?.forEach((s) => s.remove());
  return normalizeModuleContentKey(clone.textContent || "");
}

/**
 * Match / variant rows use id + slice grouping; broad module content dedup is skipped for these componentIds.
 */
export function componentIdSkipsModuleContentDedup(componentId) {
  if (componentId == null || componentId === "") return false;
  const s = String(componentId);
  return /-matchText$/.test(s) || /-variant-/.test(s);
}

/**
 * True if any on-canvas [data-content-id] for this module has the same normalized text
 * (parent claim vs related claim with identical copy share one "placed" state).
 */
export function isSameModuleContentOnCanvas(editor, moduleId, contentText) {
  if (!editor || !moduleId || contentText == null) return false;
  const want = normalizeModuleContentKey(contentText);
  if (!want) return false;
  const prefix = `${moduleId}-`;
  const doc = editor.Canvas?.getFrameEl?.()?.contentDocument;
  if (doc) {
    try {
      const nodes = doc.querySelectorAll("[data-content-id]");
      for (let i = 0; i < nodes.length; i++) {
        const cid = nodes[i].getAttribute("data-content-id");
        if (!cid || !cid.startsWith(prefix)) continue;
        if (getComparableTextFromDataContentEl(nodes[i]) === want) return true;
      }
    } catch (_) {
      /* ignore */
    }
  }
  try {
    const wrapper = editor.getWrapper?.();
    if (!wrapper || typeof wrapper.find !== "function") return false;
    const comps = wrapper.find("[data-content-id]");
    if (comps?.forEach) {
      let hit = false;
      comps.forEach((c) => {
        if (hit) return;
        const cid = c?.getAttributes?.()?.["data-content-id"];
        if (!cid || !String(cid).startsWith(prefix)) return;
        const view = c?.getEl?.();
        if (view && getComparableTextFromDataContentEl(view) === want) hit = true;
      });
      if (hit) return true;
    } else if (comps?.models?.forEach) {
      let hit = false;
      comps.models.forEach((c) => {
        if (hit) return;
        const cid = c?.getAttributes?.()?.["data-content-id"];
        if (!cid || !String(cid).startsWith(prefix)) return;
        const view = c?.getEl?.();
        if (view && getComparableTextFromDataContentEl(view) === want) hit = true;
      });
      if (hit) return true;
    }
  } catch (_) {
    /* ignore */
  }
  return false;
}

/** Deck-only: another row in the same module has the same content and used=true (non-editor contexts). */
export function isModuleContentMarkedUsedInDeck(
  selectedModuleData,
  moduleId,
  contentText,
  excludeContentId,
) {
  if (!Array.isArray(selectedModuleData) || !moduleId || contentText == null) {
    return false;
  }
  const want = normalizeModuleContentKey(contentText);
  if (!want) return false;
  return selectedModuleData.some(
    (item) =>
      item.moduleId === moduleId &&
      normalizeModuleContentKey(item.content) === want &&
      item.used === true &&
      item.id !== excludeContentId,
  );
}

/**
 * True if this content id exists on the editor (canvas iframe DOM and/or GrapesJS tree).
 */
export function isDataContentIdOnEditor(editor, contentId) {
  if (!editor || contentId == null || contentId === "") return false;
  const id = String(contentId);

  const frame = editor.Canvas?.getFrameEl?.();
  const doc = frame?.contentDocument;
  if (doc) {
    try {
      if (doc.querySelector(`[data-content-id="${id}"]`)) return true;
    } catch (_) {
      /* ignore bad selector */
    }
  }

  try {
    const wrapper = editor.getWrapper?.();
    if (wrapper && typeof wrapper.find === "function") {
      const found = wrapper.find(`[data-content-id="${id}"]`);
      const n = found?.length;
      if (typeof n === "number" && n > 0) return true;
    }
  } catch (_) {
    /* ignore */
  }

  return false;
}

/**
 * LHS row is "already on slide" if this id is on canvas, or the same normalized content
 * exists on canvas under the same module (parent + related rows with identical copy).
 * Match text / variant rows rely on id + slice grouping only (no broad content dedup).
 */
export function isSlicePlacedOnEditor(
  editor,
  selectedModuleData,
  moduleId,
  componentId,
  contentText,
) {
  if (!moduleId || !componentId) return false;
  const contentId = `${moduleId}-${componentId}`;
  const skipContentDedup = componentIdSkipsModuleContentDedup(componentId);

  if (isDataContentIdOnEditor(editor, contentId)) return true;

  const hasContent =
    contentText != null && String(contentText).trim() !== "";

  if (
    !skipContentDedup &&
    hasContent &&
    editor &&
    isSameModuleContentOnCanvas(editor, moduleId, contentText)
  ) {
    return true;
  }

  if (editor) {
    return false;
  }

  if (isContentIdUsedOnEditorOrDeck(null, selectedModuleData, contentId)) {
    return true;
  }
  if (
    !skipContentDedup &&
    hasContent &&
    isModuleContentMarkedUsedInDeck(
      selectedModuleData,
      moduleId,
      contentText,
      contentId,
    )
  ) {
    return true;
  }
  return false;
}

/** True if a parent-claim block for this claim id exists on the canvas (symbol / drag lock). */
export function parentClaimAddedOnEditor(editor, claimId) {
  if (!editor || claimId == null || claimId === "") return false;
  const allComps = editor.getWrapper?.();
  if (!allComps || typeof allComps.find !== "function") return false;
  try {
    const found = allComps.find(cssQuotedAttr("parent-claim", claimId));
    return (found?.length ?? 0) > 0;
  } catch (_) {
    return false;
  }
}

/** True if a related-claim block for this id exists on the canvas. */
export function relatedClaimAddedOnEditor(editor, relatedClaimId) {
  if (!editor || relatedClaimId == null || relatedClaimId === "") return false;
  const allComps = editor.getWrapper?.();
  if (!allComps || typeof allComps.find !== "function") return false;
  try {
    const found = allComps.find(cssQuotedAttr("related-claim", relatedClaimId));
    return (found?.length ?? 0) > 0;
  } catch (_) {
    return false;
  }
}

/** Full content id string, e.g. module-claim-footnote — canvas or deck marks used. */
export function isContentIdUsedOnEditorOrDeck(editor, selectedModuleData, contentId) {
  if (!contentId) return false;
  if (isDataContentIdOnEditor(editor, contentId)) return true;
  // With an editor instance, trust DOM/model over persisted selected_module_data.used
  // so stale flags do not keep footnotes/abbreviations grayed after remove or bad sync.
  if (editor) {
    return false;
  }
  return !!selectedModuleData?.some((item) => item.id === contentId && item.used);
}

export function isContentIdOnCanvas(canvasIds, contentId) {
  return !!(contentId && canvasIds && canvasIds.has(contentId));
}

/** Match text + all variants share one "used" group: these ids belong together. */
export function collectMatchVariantContentIds(
  moduleDam,
  parentKey,
  sliceKey,
  item,
) {
  if (!moduleDam || !parentKey || !item) return [];
  const prefix =
    sliceKey != null && sliceKey !== ""
      ? `${moduleDam}-${parentKey}-${sliceKey}`
      : `${moduleDam}-${parentKey}`;
  const ids = [];
  if (item.matchText) ids.push(`${prefix}-matchText`);
  (item.matchTextVariants || []).forEach((v) => {
    if (v?.matchText != null && v?.id != null) {
      ids.push(`${prefix}-variant-${v.id}`);
    }
  });
  return ids;
}

export function matchVariantGroupHasContent(item) {
  if (!item) return false;
  if (item.matchText) return true;
  return (item.matchTextVariants || []).some(
    (v) => v?.matchText != null && v?.id != null,
  );
}

export function matchVariantGroupAnyOnCanvas(canvasSet, moduleDam, parentKey, sliceKey, item) {
  const ids = collectMatchVariantContentIds(
    moduleDam,
    parentKey,
    sliceKey,
    item,
  );
  return ids.some((id) => canvasSet.has(id));
}

/** True if this slice still has match/variant content to place (none of the group on canvas). */
export function matchVariantGroupUnusedOnCanvas(
  canvasSet,
  moduleDam,
  parentKey,
  sliceKey,
  item,
) {
  if (!matchVariantGroupHasContent(item)) return false;
  return !matchVariantGroupAnyOnCanvas(
    canvasSet,
    moduleDam,
    parentKey,
    sliceKey,
    item,
  );
}

export function isMatchVariantGroupUsedOnEditor(
  editor,
  moduleDam,
  parentKey,
  sliceKey,
  item,
) {
  if (!editor || !item) return false;
  const canvasSet = getCanvasContentIdsSet(editor);
  return matchVariantGroupAnyOnCanvas(
    canvasSet,
    moduleDam,
    parentKey,
    sliceKey,
    item,
  );
}

/**
 * One related slice still has something draggable (aligned with LHS isSlicePlacedOnEditor + match lock).
 */
export function relatedClaimSliceHasUnusedForFilter(
  editor,
  selectedModuleData,
  moduleDam,
  parentKey,
  relatedItem,
) {
  const rc = relatedItem?.claimName || relatedItem?.reusableTextName;
  if (!rc || !moduleDam || !parentKey) return false;

  const sliceKey = normRelatedId(rc);
  const base = `${parentKey}-${sliceKey}`;

  if (matchVariantGroupHasContent(relatedItem)) {
    const locked =
      relatedClaimAddedOnEditor(editor, rc) ||
      isMatchVariantGroupUsedOnEditor(
        editor,
        moduleDam,
        parentKey,
        sliceKey,
        relatedItem,
      );
    if (!locked) return true;
  }

  if (relatedItem.footnotes) {
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        moduleDam,
        `${base}-footnote`,
        relatedItem.footnotes,
      )
    ) {
      return true;
    }
  }

  const abbrs = relatedItem.abbreviation?.split(";") || [];
  for (let i = 0; i < abbrs.length; i++) {
    if (!abbrs[i]?.trim()) continue;
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        moduleDam,
        `${base}-abbreviation-${i + 1}`,
        abbrs[i].trim(),
      )
    ) {
      return true;
    }
  }

  for (const ref of relatedItem.references || []) {
    if (ref?.id == null) continue;
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        moduleDam,
        `${base}-reference-${ref.id}`,
        ref.documentName,
      )
    ) {
      return true;
    }
  }

  return false;
}

/**
 * True if any related slice (claim / reusable under parent) still has unused LHS content,
 * with match+variants treated as one unit per slice (same rules as LHS).
 */
export function relatedItemsSubpanelHasUnusedOnEditor(
  editor,
  moduleDam,
  parentKey,
  relatedItems,
  selectedModuleData,
) {
  if (!editor || !moduleDam || !parentKey || !relatedItems?.length) {
    return false;
  }
  return relatedItems.some((rel) =>
    relatedClaimSliceHasUnusedForFilter(
      editor,
      selectedModuleData,
      moduleDam,
      parentKey,
      rel,
    ),
  );
}

/** Main claim: unused if any slice (self or related) still has draggable content (id + module content rules). */
export function mainClaimHasUnusedContentOnEditor(
  editor,
  claim,
  moduleDam,
  selectedModuleData,
) {
  if (!claim || !moduleDam || !claim.claimName) return true;
  const m = moduleDam;
  const c = claim.claimName;

  if (matchVariantGroupHasContent(claim)) {
    const locked =
      parentClaimAddedOnEditor(editor, c) ||
      isMatchVariantGroupUsedOnEditor(editor, m, c, null, claim);
    if (!locked) return true;
  }

  if (claim.footnotes) {
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        m,
        `${c}-footnote`,
        claim.footnotes,
      )
    ) {
      return true;
    }
  }

  const abbrs = claim.abbreviation?.split(";") || [];
  for (let i = 0; i < abbrs.length; i++) {
    if (!abbrs[i]?.trim()) continue;
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        m,
        `${c}-abbreviation-${i + 1}`,
        abbrs[i].trim(),
      )
    ) {
      return true;
    }
  }

  for (const ref of claim.references || []) {
    if (ref?.id == null) continue;
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        m,
        `${c}-reference-${ref.id}`,
        ref.documentName,
      )
    ) {
      return true;
    }
  }

  for (const rel of claim.relatedClaims || []) {
    if (relatedClaimSliceHasUnusedForFilter(editor, selectedModuleData, m, c, rel)) {
      return true;
    }
  }

  return false;
}

/** Component card: image, title, related claims & reusable slices. */
export function componentHasUnusedContentOnEditor(
  editor,
  component,
  moduleDam,
  selectedModuleData,
) {
  if (!component || !moduleDam || !component.componentDocNumber) return true;
  const m = moduleDam;
  const d = component.componentDocNumber;

  if (
    !isSlicePlacedOnEditor(
      editor,
      selectedModuleData,
      m,
      `${d}-componentUrl`,
      component.componentUrl || "",
    )
  ) {
    return true;
  }

  if (component.title) {
    if (
      !isSlicePlacedOnEditor(
        editor,
        selectedModuleData,
        m,
        `${d}-title`,
        component.title,
      )
    ) {
      return true;
    }
  }

  for (const rel of component.relatedClaims || []) {
    if (relatedClaimSliceHasUnusedForFilter(editor, selectedModuleData, m, d, rel)) {
      return true;
    }
  }
  for (const rel of component.relatedReusableTexts || []) {
    if (relatedClaimSliceHasUnusedForFilter(editor, selectedModuleData, m, d, rel)) {
      return true;
    }
  }

  return false;
}

/** Reusable text row: show in "Not used" filter when match text can still be placed. */
export function reusableTextHasUnusedContentOnEditor(
  editor,
  reusable,
  moduleDam,
  selectedModuleData,
) {
  if (!reusable || !moduleDam) return true;
  if (!reusable.matchText) return true;
  return !isSlicePlacedOnEditor(
    editor,
    selectedModuleData,
    moduleDam,
    `${reusable.reusableTextName}-matchText`,
    reusable.matchText,
  );
}

function collectRelatedClaimSliceIds(relatedClaim, moduleDam, parentKey) {
  const rc = relatedClaim?.claimName || relatedClaim?.reusableTextName;
  if (!rc || !moduleDam || !parentKey) return [];
  const base = `${moduleDam}-${parentKey}-${rc}`;
  const ids = [];
  if (relatedClaim.matchText) ids.push(`${base}-matchText`);
  (relatedClaim.matchTextVariants || []).forEach((v) => {
    if (v?.matchText != null && v?.id != null) {
      ids.push(`${base}-variant-${v.id}`);
    }
  });
  if (relatedClaim.footnotes) ids.push(`${base}-footnote`);
  const abbrs = relatedClaim.abbreviation?.split(";") || [];
  abbrs.forEach((raw, i) => {
    if (raw?.trim()) ids.push(`${base}-abbreviation-${i + 1}`);
  });
  (relatedClaim.references || []).forEach((ref) => {
    if (ref?.id != null) ids.push(`${base}-reference-${ref.id}`);
  });
  return ids;
}

/** All draggable content ids for a main claim (includes related claims). */
export function collectMainClaimContentIds(claim, moduleDam) {
  if (!claim || !moduleDam || !claim.claimName) return [];
  const m = moduleDam;
  const c = claim.claimName;
  const ids = [];
  if (claim.matchText) ids.push(`${m}-${c}-matchText`);
  (claim.matchTextVariants || []).forEach((v) => {
    if (v?.matchText != null && v?.id != null) {
      ids.push(`${m}-${c}-variant-${v.id}`);
    }
  });
  if (claim.footnotes) ids.push(`${m}-${c}-footnote`);
  const abbrs = claim.abbreviation?.split(";") || [];
  abbrs.forEach((raw, i) => {
    if (raw?.trim()) ids.push(`${m}-${c}-abbreviation-${i + 1}`);
  });
  (claim.references || []).forEach((ref) => {
    if (ref?.id != null) ids.push(`${m}-${c}-reference-${ref.id}`);
  });
  (claim.relatedClaims || []).forEach((rel) => {
    ids.push(...collectRelatedClaimSliceIds(rel, m, c));
  });
  return ids;
}

export function collectReusableContentIds(reusable, moduleDam) {
  if (!reusable || !moduleDam || !reusable.reusableTextName) return [];
  const ids = [];
  if (reusable.matchText) {
    ids.push(`${moduleDam}-${reusable.reusableTextName}-matchText`);
  }
  return ids;
}

export function collectComponentContentIds(component, moduleDam) {
  if (!component || !moduleDam || !component.componentDocNumber) return [];
  const m = moduleDam;
  const d = component.componentDocNumber;
  const ids = [`${m}-${d}-componentUrl`];
  if (component.title) ids.push(`${m}-${d}-title`);
  (component.relatedClaims || []).forEach((rel) => {
    ids.push(...collectRelatedClaimSliceIds(rel, m, d));
  });
  (component.relatedReusableTexts || []).forEach((rel) => {
    ids.push(...collectRelatedClaimSliceIds(rel, m, d));
  });
  return ids;
}

/** True if at least one LHS piece for this entity is not on the canvas. */
export function entityHasUnusedContentOnEditor(editor, contentIds) {
  if (!contentIds?.length) return true;
  const onCanvas = getCanvasContentIdsSet(editor);
  return contentIds.some((id) => !onCanvas.has(id));
}

/** Normalize claim / related ids (e.g. CL-006136) for comparisons */
export function normRelatedId(id) {
  if (id == null || id === "") return "";
  return String(id).trim();
}

/** Stable id for a related row in CMS (CL-006136 may live on claimName or reusableTextName) */
export function relatedClaimEntryId(rel) {
  if (!rel) return "";
  return normRelatedId(
    rel.claimName || rel.reusableTextName || rel.componentDocNumber || "",
  );
}

/**
 * Parent keys under this module that list the same related id (e.g. CL-006136 under CL-006181 and CL-006180).
 */
export function collectParentKeysSharingRelatedClaim(moduleClaimsData, relatedSliceKey) {
  const set = new Set();
  const target = normRelatedId(relatedSliceKey);
  if (!moduleClaimsData || !target) return set;

  const listsContain = (lists) => {
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const rel of list) {
        if (relatedClaimEntryId(rel) === target) return true;
      }
    }
    return false;
  };

  for (const c of moduleClaimsData.claims || []) {
    const pk = normRelatedId(c.claimName || c.componentDocNumber);
    if (pk && listsContain([c.relatedClaims, c.relatedReusableTexts])) set.add(pk);
  }
  for (const r of moduleClaimsData.reusableTexts || []) {
    const pk = normRelatedId(r.reusableTextName || r.claimName);
    if (pk && listsContain([r.relatedClaims, r.relatedReusableTexts])) set.add(pk);
  }
  for (const comp of moduleClaimsData.components || []) {
    const pk = normRelatedId(comp.componentDocNumber || comp.claimName);
    if (pk && listsContain([comp.relatedClaims, comp.relatedReusableTexts])) set.add(pk);
  }
  return set;
}

/**
 * Apply the same symbol to every slide row for module + related id (e.g. CL-006136),
 * and add rows for each parent claim (CL-006181, CL-006180) that references it in CMS.
 */
export function mergeRelatedSymbolForAllParentsInModule(
  rows,
  moduleId,
  relatedSliceKey,
  symbol,
  moduleClaimsData,
  currentParentClaimId,
) {
  const mod = normRelatedId(moduleId);
  const relId = normRelatedId(relatedSliceKey);
  if (!mod || !relId) {
    return Array.isArray(rows) ? [...rows] : [];
  }
  const draft = Array.isArray(rows) ? rows.map((r) => ({ ...r })) : [];
  const parentKeys = collectParentKeysSharingRelatedClaim(moduleClaimsData, relId);
  const curParent = normRelatedId(currentParentClaimId);
  if (curParent) parentKeys.add(curParent);

  for (let i = 0; i < draft.length; i++) {
    if (
      normRelatedId(draft[i].moduleId) === mod &&
      normRelatedId(draft[i].relatedClaimId) === relId
    ) {
      draft[i] = {
        ...draft[i],
        symbol,
        text: draft[i].text ?? "",
      };
      if (draft[i].claimId) parentKeys.add(normRelatedId(draft[i].claimId));
    }
  }

  for (const parentId of parentKeys) {
    const pid = normRelatedId(parentId);
    if (!pid) continue;
    const exists = draft.some(
      (r) =>
        normRelatedId(r.moduleId) === mod &&
        normRelatedId(r.relatedClaimId) === relId &&
        normRelatedId(r.claimId) === pid,
    );
    if (!exists) {
      draft.push({
        relatedClaimId: relId,
        claimId: pid,
        moduleId: mod,
        symbol,
        text: "",
      });
    }
  }

  return draft;
}

const supReplaceConfig = (relatedSliceKey, symbol, styleObj, hidden) => ({
  tagName: "sup",
  attributes: { "claim-id": relatedSliceKey },
  style: hidden ? { display: "none", ...(styleObj || {}) } : styleObj || {},
  content: symbol,
  editable: false,
  selectable: true,
  hoverable: true,
  stylable: false,
});

/** Update every canvas sup for this related id and each parent-claim block that uses it */
export function syncCanvasRelatedSymbolsForSharedClaim(
  editor,
  relatedSliceKey,
  symbol,
  moduleClaimsData,
  currentParentClaimId,
) {
  const relId = normRelatedId(relatedSliceKey);
  if (!editor?.getWrapper || !relId) return;
  const wrapper = editor.getWrapper();
  if (!wrapper || typeof wrapper.find !== "function") return;

  const parentKeys = collectParentKeysSharingRelatedClaim(moduleClaimsData, relId);
  const cur = normRelatedId(currentParentClaimId);
  if (cur) parentKeys.add(cur);

  try {
    const globalMatches = wrapper.find(cssQuotedAttr("claim-id", relId));
    if (globalMatches && globalMatches.length > 0) {
      for (let j = 0; j < globalMatches.length; j++) {
        const el = globalMatches[j];
        if (!el?.replaceWith) continue;
        const styleObj = el.getStyle?.() || {};
        el.replaceWith(supReplaceConfig(relId, symbol, styleObj, false));
      }
    }
  } catch (_) {
    /* ignore */
  }

  for (const parentId of parentKeys) {
    try {
      const claimParentOnEditor = wrapper.find(cssQuotedAttr("parent-claim", parentId));
      if (!claimParentOnEditor || claimParentOnEditor.length === 0) continue;
      for (let i = 0; i < claimParentOnEditor.length; i++) {
        const parentComp = claimParentOnEditor[i];
        if (!parentComp) continue;
        const symbolElements =
          typeof parentComp.find === "function"
            ? parentComp.find(cssQuotedAttr("claim-id", relId))
            : null;
        if (symbolElements && symbolElements.length > 0) {
          for (let j = 0; j < symbolElements.length; j++) {
            const symEl = symbolElements[j];
            if (!symEl?.replaceWith) continue;
            const styleObj = symEl.getStyle?.() || {};
            symEl.replaceWith(supReplaceConfig(relId, symbol, styleObj, false));
          }
        } else if (typeof parentComp.append === "function") {
          parentComp.append(supReplaceConfig(relId, symbol, {}, true));
        }
      }
    } catch (_) {
      /* ignore */
    }
  }
}

/** LHS: one symbol per module + related id — CL-006136 shows same symbol under CL-006181 and CL-006180 */
export function buildSharedRelatedSymbolLookup(assignedSymbols) {
  const map = new Map();
  if (!Array.isArray(assignedSymbols)) return map;
  for (const row of assignedSymbols) {
    const mod = normRelatedId(row?.moduleId);
    const rid = normRelatedId(row?.relatedClaimId);
    if (!mod || !rid) continue;
    const sym = row.symbol;
    if (sym == null || sym === "") continue;
    const key = `${mod}|${rid}`;
    if (!map.has(key)) map.set(key, sym);
  }
  return map;
}

export function getSymbolForRelatedInModule(rows, moduleId, relatedClaimId) {
  const mod = normRelatedId(moduleId);
  const rid = normRelatedId(relatedClaimId);
  if (!Array.isArray(rows) || !mod || !rid) return undefined;
  const forKey = rows.filter(
    (r) => normRelatedId(r.relatedClaimId) === rid && normRelatedId(r.moduleId) === mod,
  );
  if (!forKey.length) return undefined;
  const withSym = forKey.find((r) => r.symbol != null && r.symbol !== "");
  return (withSym || forKey[0]).symbol;
}

// Apply every assigned symbol from relatedClaimsData to the canvas.
// embed stale <sup> text — run after load / dimension switch so the canvas matches the DB.
export function syncAllCanvasSymbolsFromRelatedClaimsData( editor, relatedClaimsData, moduleClaimsData ) {
  if (!editor || !Array.isArray(relatedClaimsData) || relatedClaimsData.length === 0) return;
  const byKey = new Map();
  for (const row of relatedClaimsData) {
    const mod = normRelatedId(row?.moduleId);
    const rid = normRelatedId(row?.relatedClaimId);
    const sym = row?.symbol;
    if (!mod || !rid || sym == null || sym === "") continue;
    const key = `${mod}|${rid}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        rid,
        claimId: normRelatedId(row?.claimId),
        sym,
      });
    }
  }
  for (const { rid, claimId, sym } of byKey.values()) {
    syncCanvasRelatedSymbolsForSharedClaim(editor, rid, sym, moduleClaimsData, claimId);
  }
}
